import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';

// Layouts & Routing
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRouter from './components/RoleBasedRouter';
import DashboardLayout from './components/DashboardLayout';

// Auth Pages
import Login from './pages/auth/Login';
import AdvisorSignup from './pages/auth/AdvisorSignup';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import MissingProfile from './pages/auth/MissingProfile';
import ClientInvitation from './pages/auth/ClientInvitation';
import LawFirmInvitation from './pages/auth/LawFirmInvitation';
import AdvisorFirmInvitation from './pages/auth/AdvisorFirmInvitation';

// Client Pages
import CreateClient from './pages/clients/CreateClient';
import ClientList from './pages/clients/ClientList';
import ClientDetail from './pages/clients/ClientDetail';

// Matter Pages
import CreateMatter from './pages/matters/CreateMatter';
import MatterList from './pages/matters/MatterList';
import MatterDetail from './pages/matters/MatterDetail';
import ClientMatterDetail from './pages/matters/ClientMatterDetail';
import IntakePage from './pages/matters/IntakePage';
import IntakeSummaryPage from './pages/matters/IntakeSummaryPage';
import LawFirmMatterList from './pages/matters/LawFirmMatterList';
import LawFirmMatterDetail from './pages/matters/LawFirmMatterDetail';
import AttorneyReviewWorkspace from './pages/matters/AttorneyReviewWorkspace';
import DocumentWorkspace from './pages/matters/DocumentWorkspace';
import TemplateManagement from './pages/matters/TemplateManagement';
import LawFirmTaskQueue from './pages/matters/LawFirmTaskQueue';

// Billing Pages
import AdvisorBilling from './pages/billing/AdvisorBilling';

// Education Pages
import ResourcePage from './pages/education/ResourcePage';
import TopicPage from './pages/education/TopicPage';
import LearningCenterPage from './pages/education/LearningCenterPage';
import LearningTracksAdminPage from './pages/education/LearningTracksAdminPage';
import LearningContentAdminPage from './pages/education/LearningContentAdminPage';

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<AdvisorSignup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/client-invitation/:token" element={<ClientInvitation />} />
          <Route path="/law-firm-invitation/:token" element={<LawFirmInvitation />} />
          <Route path="/advisor-firm-invitation/:token" element={<AdvisorFirmInvitation />} />

          {/* Edge Case Route */}
          <Route path="/missing-profile" element={<MissingProfile />} />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardLayout />}>
              <Route path="/dashboard" element={<RoleBasedRouter />} />
              
              {/* Client Routes */}
              <Route path="/clients" element={<ClientList />} />
              <Route path="/clients/create" element={<CreateClient />} />
              <Route path="/clients/:clientId" element={<ClientDetail />} />

              {/* Matter Routes */}
              <Route path="/matters" element={<MatterList />} />
              <Route path="/matters/create" element={<CreateMatter />} />
              <Route path="/matters/:matterId" element={<MatterDetail />} />
              <Route path="/client/matters/:matterId" element={<ClientMatterDetail />} />
              <Route path="/matters/:matterId/intake" element={<IntakePage />} />
              <Route path="/matters/:matterId/intake-summary" element={<IntakeSummaryPage />} />
              <Route path="/law-firm/matters" element={<LawFirmMatterList />} />
              <Route path="/law-firm/matters/:matterId" element={<LawFirmMatterDetail />} />
              <Route path="/law-firm/matters/:matterId/review" element={<AttorneyReviewWorkspace />} />
              <Route path="/law-firm/matters/:matterId/documents" element={<DocumentWorkspace />} />
              <Route path="/law-firm/templates" element={<TemplateManagement />} />
              <Route path="/law-firm/tasks" element={<LawFirmTaskQueue />} />

              {/* Billing Routes */}
              <Route path="/billing" element={<AdvisorBilling />} />

              {/* Education Routes */}
              <Route path="/learning" element={<LearningCenterPage />} />
              <Route path="/admin/learning-content" element={<LearningContentAdminPage />} />
              <Route path="/admin/learning-tracks" element={<LearningTracksAdminPage />} />
              <Route path="/education/:slug" element={<ResourcePage />} />
              <Route path="/learning/:slug" element={<TopicPage />} />

              {/* Default redirect to dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
